import type React from 'react';

import { Avatar } from '@change/ds-core-ui/Avatar';
import { Text } from '@change/ds-core-ui/Text';

import { firstInitial } from '#src/app/shared/utils/string';

import type { ContactDecisionMakerTarget } from '../../../types';

type Props = { decisionMaker: ContactDecisionMakerTarget };

/** Who this is going to. Avatar, name, and `{title} • {location}`. */
export const RecipientCard: React.FC<Props> = ({ decisionMaker }) => {
	const { displayName, photo, title, location } = decisionMaker;

	// Either half may be missing, and the separator only earns its place when both are present —
	// otherwise the subtitle collapses rather than rendering a stray bullet.
	const subtitle = [title, location].filter(Boolean).join(' • ');

	return (
		<div
			className="border-stroke-weaker flex items-center gap-3 rounded-md border border-solid px-3.5 py-3"
			data-testid="contact-decision-maker-recipient"
		>
			{/* `src` goes straight through, and `initial` only stands in when there is no photo at all.
			    Probing the URL first meant every valid photo showed initials for a frame, and passing
			    both left a broken URL on initials rather than the DS's own fallback. */}
			<Avatar
				size="small"
				className="shrink-0"
				src={photo ?? undefined}
				initial={photo ? undefined : firstInitial(displayName)}
				alt={displayName}
			/>
			<div className="flex min-w-0 flex-col">
				<Text as="span" className="text-size-body-small-bold text-typography-strong">
					{displayName}
				</Text>
				{subtitle && (
					<Text
						as="span"
						className="text-size-body-xs text-typography-weak"
						data-testid="contact-decision-maker-recipient-subtitle"
					>
						{subtitle}
					</Text>
				)}
			</div>
		</div>
	);
};
